import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/db";
import { getAppointmentAccess } from "@/utils/accessPolicy";

export function filterFinancialRecords(appointments, facilityCharges, doctorId, financialScope, memberships = []) {
    if (!financialScope?.canAccessFinancials) {
        return { appointments: [], facilityCharges: [] };
    }

    const includedClinicIds = new Set(financialScope.includedClinicIds || []);
    const includePersonal = Boolean(financialScope.includePersonal);

    const filteredAppointments = appointments.filter(appointment => {
        const isPersonal = !appointment.clinic_id || appointment.clinic_id === "personal";
        if (isPersonal) {
            return includePersonal && appointment.doctor_id === doctorId;
        }
        if (!includedClinicIds.has(appointment.clinic_id)) return false;
        if (memberships && memberships.length > 0) {
            const access = getAppointmentAccess(appointment, doctorId, memberships);
            return access.canViewFee;
        }
        return true;
    });

    const appointmentIds = new Set(filteredAppointments.map(appointment => appointment.id));

    const filteredFacilityCharges = facilityCharges.filter(charge => {
        if (appointmentIds.has(charge.appointment_id)) return true;
        return Boolean(charge.clinic_id && includedClinicIds.has(charge.clinic_id));
    });

    return {
        appointments: filteredAppointments,
        facilityCharges: filteredFacilityCharges
    };
}

/**
 * Custom hook to fetch and aggregate reports data from local IndexedDB.
 * 
 * @param {string} doctorId - ID of the current user
 * @param {string} activeMode - Current view mode (clinic ID, 'personal', 'everything')
 * @param {Object} options - Filter options
 * @param {string} options.dateRange - 'current_month', 'last_month', '2_months_ago', '3_months_ago', or 'custom'
 * @param {Date} options.startDate - Start date for custom range
 * @param {Date} options.endDate - End date for custom range
 * @param {string} options.patientId - Filter by specific patient
 * @param {number} options.page - Current page (0-indexed)
 * @param {number} options.pageSize - Items per page
 * @param {Object} options.financialScope - Financial scope from getFinancialScope
 * @returns {Object} { chartData, transactions, totalCount, patients, isLoading }
 */
export function useReportsData(doctorId, activeMode, options = {}) {
    const {
        dateRange = 'current_month',
        startDate,
        endDate,
        patientId,
        page = 0,
        pageSize = 20,
        financialScope
    } = options;

    const financialClinicIdsKey = [...(financialScope?.includedClinicIds || [])].sort().join("|");

    const result = useLiveQuery(async () => {
        if (!doctorId) return { chartData: [], transactions: [], totalCount: 0, patients: [] };

        // Fetch all required data
        const [appointments, commissions, transactionPatients, memberships, users, clinics] = await Promise.all([
            db.appointments.toArray(),
            db.facility_charges.toArray(),
            db.patients.filter(p => !p.deleted).toArray(),
            db.clinic_members.where('user_id').equals(doctorId).filter(m => m.is_active !== false).toArray(),
            db.users.toArray(),
            db.clinics.toArray()
        ]);

        const effectiveScope = financialScope || (
            activeMode === 'everything'
                ? { canAccessFinancials: true, includePersonal: true, includedClinicIds: memberships.map(m => m.clinic_id) }
                : activeMode === 'personal'
                ? { canAccessFinancials: true, includePersonal: true, includedClinicIds: [] }
                : { canAccessFinancials: true, includePersonal: false, includedClinicIds: [activeMode] }
        );

        if (!effectiveScope.canAccessFinancials) {
            return { chartData: [], transactions: [], totalCount: 0, patients: [] };
        }

        // Get date range boundaries
        const now = new Date();
        const { rangeStart, rangeEnd } = getDateRange(dateRange, startDate, endDate, now);

        const { appointments: filteredApps, facilityCharges: filteredComms } = filterFinancialRecords(
            appointments,
            commissions,
            doctorId,
            effectiveScope,
            memberships
        );

        const transactions = buildTransactions(
            filteredApps, filteredComms, transactionPatients, users, clinics,
            doctorId, rangeStart, rangeEnd, patientId
        );

        // Get unique patients from transactions for the patient filter dropdown
        const transactionPatientsFiltered = getUniquePatients(transactions, transactionPatients);

        // Build chart data (last 3 months)
        const chartData = buildChartData(filteredApps, filteredComms, doctorId, now, clinics);

        // Paginate transactions
        const totalCount = transactions.length;
        const paginatedTransactions = transactions.slice(page * pageSize, (page + 1) * pageSize);

        return {
            chartData,
            transactions: paginatedTransactions,
            totalCount,
            patients: transactionPatientsFiltered
        };
    }, [doctorId, activeMode, dateRange, startDate?.getTime(), endDate?.getTime(), patientId, page, pageSize, financialScope?.canAccessFinancials, financialScope?.includePersonal, financialClinicIdsKey]);

    return {
        chartData: result?.chartData || [],
        transactions: result?.transactions || [],
        totalCount: result?.totalCount || 0,
        patients: result?.patients || [],
        isLoading: result === undefined
    };
}

/**
 * Get date range boundaries based on preset or custom range.
 */
export function getDateRange(dateRange, customStart, customEnd, now) {
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const twoMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);
    const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);

    switch (dateRange) {
        case 'current_month':
            return {
                rangeStart: currentMonth,
                rangeEnd: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59)
            };
        case 'last_month':
            return {
                rangeStart: lastMonth,
                rangeEnd: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)
            };
        case '2_months_ago':
            return {
                rangeStart: twoMonthsAgo,
                rangeEnd: new Date(now.getFullYear(), now.getMonth() - 1, 0, 23, 59, 59)
            };
        case '3_months_ago':
            return {
                rangeStart: threeMonthsAgo,
                rangeEnd: new Date(now.getFullYear(), now.getMonth() - 2, 0, 23, 59, 59)
            };
        case 'custom':
            return { rangeStart: customStart || currentMonth, rangeEnd: customEnd || now };
        default:
            return { rangeStart: currentMonth, rangeEnd: now };
    }
}

/**
 * Build transaction entries from appointments and facility_charges.
 */
export function buildTransactions(appointments, facility_charges, patients, users, clinics, doctorId, rangeStart, rangeEnd, filterPatientId) {
    const transactions = [];
    const patientsMap = new Map(patients.map(p => [p.id, p]));
    const usersMap = new Map(users.map(u => [u.id, u]));
    const clinicsMap = new Map((clinics || []).map(c => [c.id, c]));
    const chargesMap = new Map(facility_charges.map(c => [c.appointment_id, c]));

    // Process completed appointments
    for (const apt of appointments) {
        if (apt.status !== 'completed') continue;

        const aptDate = new Date(apt.date_time);
        if (aptDate < rangeStart || aptDate > rangeEnd) continue;

        const patient = patientsMap.get(apt.patient_id);
        if (filterPatientId && apt.patient_id !== filterPatientId) continue;

        const facility_charge = chargesMap.get(apt.id);
        const isMyAppointment = apt.doctor_id === doctorId;

        // Appointment earning row (for my appointments)
        if (isMyAppointment) {
            // I owe money only if I don't own (really own, not just placeholder) the clinic where the appointment took place
            let isClinicOwner = false;
            if (apt.clinic_id) {
                const clinic = clinicsMap.get(apt.clinic_id);
                // Placeholder clinics: creator is set as owner temporarily but is NOT the real owner
                if (clinic?.owner_doctor_id === doctorId && !clinic?.is_placeholder) {
                    isClinicOwner = true;
                }
            }
            const facilityChargePayable = (facility_charge?.associated_doctor_id === doctorId && !isClinicOwner)
                ? facility_charge?.charge_amount || 0
                : 0;

            // The receiver label: always use the clinic name for consistency
            let receiverDoctorName = null;
            if (facility_charge?.clinic_id) {
                const clinic = clinicsMap.get(facility_charge.clinic_id);
                receiverDoctorName = clinic?.name || null;
            }

            transactions.push({
                id: `apt-${apt.id}`,
                type: 'appointment',
                date: apt.date_time,
                patientId: apt.patient_id,
                patientName: patient?.name || '—',
                appointmentEarnings: apt.fee_amount || 0,
                facilityChargeEarned: 0,
                facilityChargeEarnedFromDoctor: null,
                facilityChargePayable: facilityChargePayable,
                facilityChargePayableToDoctor: receiverDoctorName || null,
                totalEarning: (apt.fee_amount || 0) - facilityChargePayable,
                appointmentId: apt.id
            });
        }
    }

    // Process facility_charge earnings (I'm the receiver if it belongs to a clinic I manage/own, or if they explicitly owe it to me, though schema usually pins it to clinic)
    for (const comm of facility_charges) {
        // If I owe it (and don't own the clinic), I'm definitely the payer, handled above.
        // Wait, what if I owe it, BUT I own the clinic? Then I'm BOTH. It's an internal earning.
        // Do we want an explicit internal earning row? Yes, because "My Facility Earnings" in the new design shows it.
        // I earn it if I am the owner of the clinic it belongs to
        let isReceivable = false;
        if (comm.clinic_id) {
            const clinic = clinicsMap.get(comm.clinic_id);
            // Placeholder clinics: creator is set as owner temporarily but is NOT the real receiver
            if (clinic?.owner_doctor_id === doctorId && !clinic?.is_placeholder) {
                isReceivable = true;
            }
        }

        if (!isReceivable) continue; // Not an earning for me

        // Use appointment_date from facility_charge (enriched during sync), or fall back to local appointment
        const apt = appointments.find(a => a.id === comm.appointment_id);
        const aptDate = comm.appointment_date ? new Date(comm.appointment_date)
            : apt?.date_time ? new Date(apt.date_time) : null;
        if (!aptDate) continue;

        if (aptDate < rangeStart || aptDate > rangeEnd) continue;

        // Only count completed appointments (use enriched status or look up local appointment)
        const aptStatus = comm.appointment_status || apt?.status;
        if (aptStatus !== 'completed') continue;

        // Try to get appointment for patient ID filtering
        if (filterPatientId && apt?.patient_id !== filterPatientId) continue;

        const patientName = comm.patient_name || (apt ? patientsMap.get(apt.patient_id)?.name : null) || '—';
        const treatingDoctorName = comm.treating_doctor_name || usersMap.get(comm.associated_doctor_id)?.name || null;

        transactions.push({
            id: `comm-${comm.id}`,
            type: 'facility_charge',
            date: comm.appointment_date,
            patientId: apt?.patient_id,
            patientName: patientName,
            appointmentEarnings: 0,
            facilityChargeEarned: comm.charge_amount || 0,
            facilityChargeEarnedFromDoctor: treatingDoctorName,
            facilityChargePayable: 0,
            facilityChargePayableToDoctor: null,
            totalEarning: comm.charge_amount || 0,
            appointmentId: comm.appointment_id
        });
    }

    // Sort by date (most recent first)
    transactions.sort((a, b) => new Date(b.date) - new Date(a.date));

    return transactions;
}

/**
 * Get unique patients from transactions for filter dropdown.
 */
function getUniquePatients(transactions, allPatients) {
    const patientIds = new Set(transactions.map(t => t.patientId).filter(Boolean));
    return allPatients
        .filter(p => patientIds.has(p.id))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
}

/**
 * Build chart data for the last 3 months.
 */
function buildChartData(appointments, facility_charges, doctorId, now, clinics = []) {
    const months = [];
    const clinicsMap = new Map((clinics || []).map(c => [c.id, c]));

    for (let i = 2; i >= 0; i--) {
        const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59);
        const monthName = monthDate.toLocaleString('default', { month: 'short', year: '2-digit' });

        let appointmentEarnings = 0;
        let facilityChargeEarned = 0;
        let facilityChargePayable = 0;

        // Calculate appointment earnings
        for (const apt of appointments) {
            if (apt.status !== 'completed') continue;
            if (apt.doctor_id !== doctorId) continue;

            const aptDate = new Date(apt.date_time);
            if (aptDate >= monthDate && aptDate <= monthEnd) {
                appointmentEarnings += apt.fee_amount || 0;
            }
        }

        // Calculate facility_charges
        for (const comm of facility_charges) {
            // Try to find appointment, but also support facility_charge receivers who may not have it
            const apt = appointments.find(a => a.id === comm.appointment_id);

            // Use appointment_date from facility_charge (enriched during sync) if apt not found
            const aptDateStr = apt?.date_time || comm.appointment_date;
            if (!aptDateStr) continue;

            const aptDate = new Date(aptDateStr);
            if (aptDate < monthDate || aptDate > monthEnd) continue;

            let isReceivable = false;
            if (comm.clinic_id) {
                const clinic = clinicsMap.get(comm.clinic_id);
                // Placeholder clinics: creator is set as owner temporarily but is NOT the real receiver
                if (clinic?.owner_doctor_id === doctorId && !clinic?.is_placeholder) {
                    isReceivable = true;
                }
            }
            const isPayable = comm.associated_doctor_id === doctorId && !isReceivable;

            // For facility_charge earned, only count completed appointments
            if (isReceivable) {
                // I earn this facility_charge (I'm the payee) - check status from apt or enriched field
                const status = apt?.status || comm.appointment_status;
                if (status === 'completed') {
                    facilityChargeEarned += comm.charge_amount || 0;
                }
            }

            if (isPayable && apt?.doctor_id === doctorId) {
                // I pay this facility_charge (only if I have the appointment = I'm the treating doctor)
                if (apt.status === 'completed') {
                    facilityChargePayable += comm.charge_amount || 0;
                }
            }
        }

        months.push({
            month: monthName,
            appointmentEarnings,
            facilityChargeEarned,
            facilityChargePayable,
            netEarnings: appointmentEarnings + facilityChargeEarned - facilityChargePayable
        });
    }

    return months;
}

/**
 * Group transactions by patient for PDF export.
 */
export function groupTransactionsByPatient(transactions) {
    const grouped = new Map();

    for (const tx of transactions) {
        const patientKey = tx.patientId || 'unknown';
        if (!grouped.has(patientKey)) {
            grouped.set(patientKey, {
                patientName: tx.patientName || 'Unknown',
                patientId: tx.patientId,
                transactions: [],
                totals: {
                    appointmentEarnings: 0,
                    facilityChargeEarned: 0,
                    facilityChargePayable: 0,
                    totalEarning: 0
                }
            });
        }

        const group = grouped.get(patientKey);
        group.transactions.push(tx);
        group.totals.appointmentEarnings += tx.appointmentEarnings;
        group.totals.facilityChargeEarned += tx.facilityChargeEarned;
        group.totals.facilityChargePayable += tx.facilityChargePayable;
        group.totals.totalEarning += tx.totalEarning;
    }

    // Sort by patient name
    return Array.from(grouped.values()).sort((a, b) =>
        (a.patientName || '').localeCompare(b.patientName || '')
    );
}

/**
 * Calculate clinic-wise total facility charges payable.
 * @param {Array} transactions
 * @returns {Array} Array of { clinicName, amount, formattedAmount }
 */
export function calculateClinicPayables(transactions) {
    const clinicPayableMap = new Map();
    for (const tx of transactions) {
        if (tx.facilityChargePayable > 0 && tx.facilityChargePayableToDoctor) {
            const clinicName = tx.facilityChargePayableToDoctor;
            const current = clinicPayableMap.get(clinicName) || 0;
            clinicPayableMap.set(clinicName, current + tx.facilityChargePayable);
        }
    }

    // Convert map to sorted array (amounts are in paise; caller should use toRupees() for display)
    return Array.from(clinicPayableMap.entries())
        .map(([clinicName, amount]) => ({ clinicName, amount }))
        .sort((a, b) => a.clinicName.localeCompare(b.clinicName));
}
